import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Briefcase, Languages, Linkedin, Loader2, Check, CalendarClock, Repeat } from "lucide-react";
import api from "@/lib/api";
import { money, useSite } from "@/lib/site";
import Navbar from "@/components/Navbar";
import { SiteFooter } from "@/components/Brand";
import { MentorAvatar } from "@/components/MentorCard";
import { Stars, ReviewCard } from "@/components/Reviews";
import { Button } from "@/components/ui/button";

const PlanCard = ({ icon: Icon, title, price, per, bullets, onPick, highlight, testId }) => (
  <div className={`rounded-3xl border p-6 flex flex-col ${highlight ? "bg-[#0B2F55] border-[#0B2F55] text-[#F5F1E8]" : "bg-white border-slate-200 text-slate-900"}`} data-testid={testId}>
    <div className="flex items-center gap-2.5">
      <Icon className={`w-5 h-5 ${highlight ? "text-sky-300" : "text-primary"}`} />
      <h3 className="font-heading font-bold text-lg">{title}</h3>
    </div>
    <p className="font-heading font-extrabold text-3xl mt-4">{price} <span className={`font-body font-normal text-sm ${highlight ? "text-[#F5F1E8]/60" : "text-slate-400"}`}>{per}</span></p>
    <ul className="space-y-2 mt-5 mb-6">
      {bullets.map((b) => <li key={b} className={`font-body text-sm flex items-start gap-2 ${highlight ? "text-[#F5F1E8]/80" : "text-slate-600"}`}><Check className={`w-4 h-4 mt-0.5 shrink-0 ${highlight ? "text-sky-300" : "text-emerald-500"}`} /> {b}</li>)}
    </ul>
    <Button onClick={onPick} data-testid={`${testId}-btn`}
      className={`mt-auto rounded-full h-11 font-heading font-bold ${highlight ? "bg-[#F5F1E8] text-[#0B2F55] hover:bg-white" : ""}`}>
      Pay via UPI & connect
    </Button>
  </div>
);

const MentorProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const site = useSite();
  const [m, setM] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { api.get(`/mentors/${slug}`).then((r) => setM(r.data)).catch(() => setErr("This mentor profile isn't available.")); }, [slug]);

  if (err) return (
    <div className="min-h-screen bg-[#F8FAFC]"><Navbar /><div className="max-w-3xl mx-auto px-5 py-24 text-center" data-testid="mentor-not-found">
      <p className="font-heading font-bold text-xl text-slate-900">{err}</p><Link to="/mentors" className="text-primary font-bold mt-4 inline-block">Back to mentors</Link></div></div>
  );
  if (!m) return <div className="min-h-screen bg-[#F8FAFC]"><Navbar /><div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></div>;

  const cur = m.currency || site.currency || "INR";
  const pick = (plan) => navigate(`/checkout?type=mentor&mentor=${m.slug}&plan=${plan}`);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <Link to="/mentors" className="inline-flex items-center gap-1.5 font-body text-sm text-slate-500 hover:text-primary" data-testid="back-to-mentors"><ArrowLeft className="w-4 h-4" /> All mentors</Link>
        <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-10 mt-6">
          <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="mentor-profile">
            <div className="flex flex-col sm:flex-row sm:items-start gap-6">
              <MentorAvatar mentor={m} className="w-28 h-28 text-3xl" />
              <div className="min-w-0">
                <h1 className="font-heading font-extrabold text-3xl sm:text-4xl text-slate-900" data-testid="mentor-name">{m.name}</h1>
                <p className="font-body text-base md:text-lg text-slate-600 mt-2" data-testid="mentor-headline">{m.headline}</p>
                {m.review_count > 0 && (
                  <div className="flex items-center gap-2 mt-2" data-testid="mentor-rating">
                    <Stars rating={m.rating} /> <span className="font-heading font-bold text-slate-900">{Number(m.rating).toFixed(1)}</span>
                    <span className="font-body text-sm text-slate-500">· {m.review_count} review{m.review_count === 1 ? "" : "s"}</span>
                  </div>
                )}
                <div className="flex flex-wrap gap-4 mt-3 font-body text-sm text-slate-500">
                  {m.experience_years > 0 && <span className="inline-flex items-center gap-1.5"><Briefcase className="w-4 h-4" /> {m.experience_years}+ years</span>}
                  {m.languages && <span className="inline-flex items-center gap-1.5"><Languages className="w-4 h-4" /> {m.languages}</span>}
                  {m.linkedin && <a href={m.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-primary hover:underline" data-testid="mentor-linkedin"><Linkedin className="w-4 h-4" /> LinkedIn</a>}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-6">
              {(m.expertise || []).map((t) => <span key={t} className="font-body text-sm px-3 py-1.5 rounded-full bg-primary/10 text-primary">{t}</span>)}
            </div>
            <div className="mt-8 bg-white rounded-3xl border border-slate-200 p-7">
              <h2 className="font-heading font-bold text-lg text-slate-900 mb-3">About {m.name.split(" ")[0]}</h2>
              <p className="font-body text-base text-slate-600 leading-relaxed whitespace-pre-line" data-testid="mentor-bio">{m.bio}</p>
            </div>
            {m.reviews?.length > 0 && (
              <div className="mt-8" data-testid="mentor-reviews">
                <h2 className="font-heading font-bold text-lg text-slate-900 mb-4">What mentees say</h2>
                <div className="grid sm:grid-cols-2 gap-4">{m.reviews.map((r) => <ReviewCard key={r.review_id} r={r} />)}</div>
              </div>
            )}
          </motion.section>

          <aside className="space-y-5 lg:sticky lg:top-24 self-start" data-testid="mentor-plans">
            {m.session_price > 0 && (
              <PlanCard icon={CalendarClock} title="Single session" price={money(m.session_price, cur)} per={`/ ${m.session_minutes || 45} min`} testId="plan-session"
                bullets={["One focused 1:1 conversation", "Bring your questions, leave with clarity", "Scheduled within 48 hours of confirmation"]} onPick={() => pick("session")} />
            )}
            {m.monthly_price > 0 && (
              <PlanCard icon={Repeat} title="Monthly mentorship" price={money(m.monthly_price, cur)} per="/ month" highlight testId="plan-monthly"
                bullets={m.monthly_includes ? m.monthly_includes.split("\n").filter(Boolean) : ["Regular sessions through the month", "Chat support between sessions", "Goal tracking and accountability"]} onPick={() => pick("monthly")} />
            )}
            {m.session_price <= 0 && m.monthly_price <= 0 && (
              <div className="rounded-3xl border border-dashed border-slate-300 p-6 font-body text-sm text-slate-500" data-testid="plans-unavailable">Pricing coming soon. Reach out via our team to connect.</div>
            )}
          </aside>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default MentorProfile;
